import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import OrderCard from './components/OrderCard';
import OrderDetailsModal from './components/OrderDetailsModal';
import FilterTabs from './components/FilterTabs';
import EmptyState from './components/EmptyState';
import StatsHeader from './components/StatsHeader';
import QuickActions from './components/QuickActions';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const TechnicianMobileView = () => {
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState('all');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState(new Date());

  // Mock technician data
  const technicianName = "Max Müller";
  const technicianId = "TECH001";

  // Mock orders data
  const [orders, setOrders] = useState([
    {
      id: "ORD-2024-001",
      orderNumber: "2024-001",
      vin: "WBAVA31070NL12345",
      status: "ASSIGNED",
      priority: "HIGH",
      serviceType: "Inspektion + Ölwechsel",
      description: "Große Inspektion fällig, Ölwechsel und Filterwechsel erforderlich",
      createdAt: "2024-08-30T08:30:00Z",
      scheduledDate: "2024-08-30T14:00:00Z",
      assignedTechnician: technicianId,
      vehicle: {
        make: "BMW",
        model: "3er Touring",
        year: 2020,
        color: "Schwarz Metallic",
        engine: "2.0L TwinPower Turbo",
        mileage: 45000,
        licensePlate: "K-MW 1234"
      },
      dealership: {
        id: "DEAL-001",
        name: "BMW Autohaus Köln",
        address: {
          street: "Aachener Str. 123",
          zipCode: "50931",
          city: "Köln"
        },
        phone: "+49 221 123456"
      },
      customerContact: {
        name: "Thomas Weber",
        phone: "+49 172 1234567",
        email: "thomas.weber@email.com"
      },
      serviceItems: [
        { description: "Große Inspektion", price: 299.00 },
        { description: "Motoröl 5W-30", price: 89.00 },
        { description: "Ölfilter", price: 25.00 },
        { description: "Luftfilter", price: 35.00 }
      ],
      workNotes: "",
      photos: []
    },
    {
      id: "ORD-2024-002",
      orderNumber: "2024-002",
      vin: "WAUZZZ8V2KA123456",
      status: "IN_PROGRESS",
              priority: "NORMAL",
      serviceType: "Bremsenservice",
      description: "Bremsbeläge vorne wechseln, Bremsscheiben prüfen",
      createdAt: "2024-08-30T07:15:00Z",
      scheduledDate: "2024-08-30T10:00:00Z",
      assignedTechnician: technicianId,
      vehicle: {
        make: "Audi",
        model: "A4 Avant",
        year: 2019,
        color: "Weiß",
        engine: "2.0 TFSI",
        mileage: 62000,
        licensePlate: "K-AU 5678"
      },
      dealership: {
        id: "DEAL-002",
        name: "Audi Zentrum Düsseldorf",
        address: {
          street: "Königsallee 45",
          zipCode: "40212",
          city: "Düsseldorf"
        },
        phone: "+49 211 987654"
      },
      customerContact: {
        name: "Sandra Klein",
        phone: "+49 173 9876543",
        email: "sandra.klein@email.com"
      },
      serviceItems: [
        { description: "Bremsbeläge vorne", price: 120.00 },
        { description: "Arbeitszeit", price: 180.00 }
      ],
      workNotes: "Bremsbeläge sind stark abgenutzt. Bremsscheiben noch in Ordnung.",
      photos: []
    },
    {
      id: "ORD-2024-003",
      orderNumber: "2024-003",
      vin: "WDD2130421A123456",
      status: "DONE",
      priority: "LOW",
      serviceType: "Klimaservice",
      description: "Klimaanlage reinigen und desinfizieren",
      createdAt: "2024-08-29T16:20:00Z",
      scheduledDate: "2024-08-30T09:00:00Z",
      assignedTechnician: technicianId,
      completedAt: "2024-08-30T10:30:00Z",
      vehicle: {
        make: "Mercedes-Benz",
        model: "C-Klasse",
        year: 2021,
        color: "Silber",
        engine: "1.5L Hybrid",
        mileage: 28000,
        licensePlate: "K-MB 9012"
      },
      dealership: {
        id: "DEAL-003",
        name: "Mercedes-Benz Bonn",
        address: {
          street: "Bonner Str. 78",
          zipCode: "53111",
          city: "Bonn"
        },
        phone: "+49 228 555123"
      },
      customerContact: {
        name: "Michael Schmidt",
        phone: "+49 174 5551234",
        email: "m.schmidt@email.com"
      },
      serviceItems: [
        { description: "Klimaservice komplett", price: 149.00 }
      ],
      workNotes: "Klimaanlage gereinigt und desinfiziert. Filter gewechselt. Funktioniert einwandfrei.",
      photos: []
    }
  ]);

  // Filter orders based on active filter
  const filteredOrders = orders?.filter(order => {
    if (activeFilter === 'all') return true;
    if (activeFilter === 'assigned') return order?.status === 'ASSIGNED';
    if (activeFilter === 'in_progress') return order?.status === 'IN_PROGRESS';
    if (activeFilter === 'done') return order?.status === 'DONE';
    return true;
  });

  // Calculate order counts for tabs
  const orderCounts = {
    all: orders?.length,
    assigned: orders?.filter(o => o?.status === 'ASSIGNED')?.length,
    inProgress: orders?.filter(o => o?.status === 'IN_PROGRESS')?.length,
    done: orders?.filter(o => o?.status === 'DONE')?.length
  };

  // Calculate stats for header
  const stats = {
    assigned: orderCounts?.assigned,
    inProgress: orderCounts?.inProgress,
    completed: orderCounts?.done
  };

  const handleStatusUpdate = async (orderId, newStatus) => {
    setOrders(prevOrders => 
      prevOrders?.map(order => 
        order?.id === orderId 
          ? { 
              ...order, 
              status: newStatus,
              ...(newStatus === 'DONE' ? { completedAt: new Date()?.toISOString() } : {})
            }
          : order
      )
    );

    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Show success notification (in real app, use toast)
  };

  const handleViewDetails = (order) => {
    setSelectedOrder(order);
    setIsModalOpen(true);
  };

  const handleCallCustomer = (phoneNumber) => {
    // In real app, this would initiate a phone call
    window.location.href = `tel:${phoneNumber}`;
  };

  const handleSaveNotes = async (orderId, data) => {
    setOrders(prevOrders => 
      prevOrders?.map(order => 
        order?.id === orderId 
          ? { 
              ...order, 
              workNotes: data?.workNotes,
              vehicle: { ...order?.vehicle, mileage: data?.mileage },
              photos: [...order?.photos, ...data?.photos]
            }
          : order
      )
    );

    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    // Simulate API refresh
    await new Promise(resolve => setTimeout(resolve, 1000));
    setLastSyncTime(new Date());
    setIsRefreshing(false);
  };

  const handleScanVin = () => {
    navigate('/vin-scanner');
  };

  const handleViewProfile = () => {
    // In real app, navigate to profile page
  };

  const handleSyncData = async () => {
    await handleRefresh();
  };

  // Custom breadcrumb for technician view
  const breadcrumbItems = [
    { label: 'Techniker', path: '/technician-assignment', icon: 'Users' },
    { label: 'Mobile Ansicht', path: '/technician-mobile-view', icon: 'Smartphone', current: true }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header userRole="admin" />
      <main className="pt-16 pb-20 lg:pb-6">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Desktop Breadcrumb */}
          <div className="hidden lg:block">
            <Breadcrumb customItems={breadcrumbItems} />
          </div>

          {/* Stats Header */}
          <StatsHeader stats={stats} technicianName={technicianName} />

          {/* Quick Actions */}
          <QuickActions
            onScanVin={handleScanVin}
            onViewProfile={handleViewProfile}
            onSyncData={handleSyncData}
            lastSyncTime={lastSyncTime}
          />

          {/* Filter Tabs */}
          <FilterTabs
            activeFilter={activeFilter}
            onFilterChange={setActiveFilter}
            orderCounts={orderCounts}
          />

          {/* Refresh Button */}
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold">
              {activeFilter === 'all' && 'Alle Aufträge'}
              {activeFilter === 'assigned' && 'Zugewiesene Aufträge'}
              {activeFilter === 'in_progress' && 'Aufträge in Bearbeitung'}
              {activeFilter === 'done' && 'Abgeschlossene Aufträge'}
            </h2>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleRefresh}
              loading={isRefreshing}
              iconName="RefreshCw"
            >
              <span className="hidden sm:inline ml-2">Aktualisieren</span>
            </Button>
          </div>

          {/* Orders List */}
          <div className="space-y-4">
            {filteredOrders?.length > 0 ? (
              filteredOrders?.map((order) => (
                <OrderCard
                  key={order?.id}
                  order={order}
                  onStatusUpdate={handleStatusUpdate}
                  onViewDetails={handleViewDetails}
                  onCallCustomer={handleCallCustomer}
                />
              ))
            ) : (
              <EmptyState filter={activeFilter} onRefresh={handleRefresh} />
            )}
          </div>
        </div>
      </main>
      {/* Order Details Modal */}
      <OrderDetailsModal
        order={selectedOrder}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onStatusUpdate={handleStatusUpdate}
        onSaveNotes={handleSaveNotes}
      />
      {/* Mobile Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-card border-t border-border lg:hidden">
        <div className="grid grid-cols-4 h-16">
          <button
            onClick={() => navigate('/order-management-dashboard')}
            className="flex flex-col items-center justify-center space-y-1 text-text-secondary hover:text-primary transition-colors"
          >
            <Icon name="ClipboardList" size={20} />
            <span className="text-xs">Aufträge</span>
          </button>
          <button
            onClick={() => navigate('/vin-scanner')}
            className="flex flex-col items-center justify-center space-y-1 text-text-secondary hover:text-primary transition-colors"
          >
            <Icon name="Scan" size={20} />
            <span className="text-xs">Scanner</span>
          </button>
          <button
            onClick={() => navigate('/technician-mobile-view')}
            className="flex flex-col items-center justify-center space-y-1 text-primary"
          >
            <Icon name="Smartphone" size={20} />
            <span className="text-xs">Meine Aufträge</span>
          </button>
          <button
            onClick={handleViewProfile}
            className="flex flex-col items-center justify-center space-y-1 text-text-secondary hover:text-primary transition-colors"
          >
            <Icon name="User" size={20} />
            <span className="text-xs">Profil</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default TechnicianMobileView;